import React from "react";
import { Check, Loader2, Lock, Shuffle as ShuffleIcon, Unlock } from "lucide-react";
import { SITEDETAIL } from "@/constants/testIds";
import { DIMENSIONS } from "./catalog";
import { Flash, ScaledPreview } from "./ThemeTile";

const SHUFFLE_PREVIEW_W = 980;
const SHUFFLE_PREVIEW_H = 620;
const SHUFFLE_PREVIEW_SCALE = 0.5; // 1960 → 980

function LockRow({ locks, busy, onToggleLock }) {
  const lockedCount = Object.values(locks).filter(Boolean).length;
  const free = 5 - lockedCount;
  return (
    <div className="flex items-center gap-2 mb-3 flex-wrap">
      <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Lock</span>
      {DIMENSIONS.map((k) => (
        <button key={k} data-testid={SITEDETAIL.themeShuffleLock(k)} onClick={() => onToggleLock(k)} disabled={!!busy} aria-pressed={!!locks[k]}
          title={locks[k] ? `Unlock ${k}` : `Lock ${k} to current value`}
          className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border transition disabled:opacity-40 ${
            locks[k] ? "border-amber-500/60 bg-amber-500/15 text-amber-200" : "border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-zinc-200"}`}>
          {locks[k] ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />} {k}
        </button>
      ))}
      {lockedCount > 0 && (
        <span className="text-[10px] text-zinc-500 mono ml-1">{lockedCount === 5 ? "all locked" : `${free} dimension${free === 1 ? "" : "s"} will reroll`}</span>
      )}
    </div>
  );
}

function Chip({ kind, item, locked }) {
  return (
    <span data-testid={SITEDETAIL.themeShuffleChip(kind)}
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-sm border ${
        locked ? "bg-amber-500/10 border-amber-500/40 text-amber-200" : "bg-zinc-800/60 border-zinc-700 text-zinc-200"}`}>
      {locked && <Lock className="w-3 h-3 text-amber-400" />}
      <span className="text-zinc-500">{kind}</span>
      {item.swatch && (
        <span className="inline-flex h-3 w-6 rounded-sm overflow-hidden border border-zinc-700">
          <span className="flex-1" style={{ background: item.swatch.bg }} /><span className="flex-1" style={{ background: item.swatch.accent }} />
        </span>
      )}
      <span className={`normal-case font-bold ${locked ? "text-amber-100" : "text-zinc-100"}`}>{item.label}</span>
    </span>
  );
}

function Empty() {
  return (
    <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center bg-zinc-950/40">
      <ShuffleIcon className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
      <div className="text-sm text-zinc-400 mb-1">No shuffle yet.</div>
      <div className="text-xs text-zinc-600 mono">Click <span className="text-zinc-300">Shuffle</span> above to roll a random combination across all 5 dimensions.</div>
    </div>
  );
}

/** Shuffle tab: lock row, rolled chips, Shuffle / Apply all, one big combined preview. */
export function ShuffleView({ s, bust, busy, flash }) {
  const { shuffle, locks } = s;
  const allLocked = Object.values(locks).every(Boolean);
  return (
    <div className="p-5">
      <LockRow locks={locks} busy={busy} onToggleLock={s.toggleLock} />
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {shuffle ? DIMENSIONS.map((k) => <Chip key={k} kind={k} item={shuffle[k]} locked={!!locks[k]} />)
            : <div className="text-xs text-zinc-500 mono">Roll the dice on a fresh combination — locked dimensions stay put.</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid={SITEDETAIL.themeShuffleRoll} onClick={s.roll} disabled={!!busy || allLocked} title={allLocked ? "Unlock at least one dimension to shuffle" : ""}
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border border-zinc-700 hover:border-zinc-500 text-zinc-200 disabled:opacity-40">
            <ShuffleIcon className="w-3.5 h-3.5" /> {shuffle ? "Shuffle again" : "Shuffle"}
          </button>
          <button data-testid={SITEDETAIL.themeShuffleApply} onClick={s.applyAll} disabled={!shuffle || !!busy || allLocked}
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm bg-amber-500/20 border border-amber-500/40 hover:bg-amber-500/30 text-amber-200 disabled:opacity-40">
            {busy === "shuffle:all" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Apply all
          </button>
        </div>
      </div>
      {shuffle ? (
        <div className="border border-zinc-800 rounded-sm overflow-hidden bg-zinc-950" style={{ width: "100%", maxWidth: SHUFFLE_PREVIEW_W, margin: "0 auto" }}>
          <ScaledPreview src={s.url(bust)} title="Shuffle preview" iframeKey={bust} w={SHUFFLE_PREVIEW_W} h={SHUFFLE_PREVIEW_H} scale={SHUFFLE_PREVIEW_SCALE} />
        </div>
      ) : <Empty />}
      <Flash flash={flash} />
    </div>
  );
}
